import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import {
  ArrowForward,
  Visibility,
  VisibilityOff,
  Refresh,
  LockOutlined,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../constants/constants";
import { SILKCART_LOGO } from "../theme/theme";
import { saveToken } from "../utils/util";
import { useNavigate } from "react-router-dom";
import "./adminLogin.css";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const [captchaSvg, setCaptchaSvg] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaError("");
    setCaptchaToken("");
    setCaptchaSvg("");
    setCaptcha("");
    try {
      const response = await fetch(`https://apis.mysilkcart.com/api/captcha`);
      if (!response.ok)
        throw new Error(
          "Unable to load the verification code. Please try again."
        );
      const { data, token } = await response.json();
      if (!data || !token)
        throw new Error(
          "Unable to load the verification code. Please try again."
        );
      setCaptchaSvg(data);
      setCaptchaToken(token);
    } catch (err) {
      setCaptchaError(err.message);
    } finally {
      setCaptchaLoading(false);
    }
  };
  useEffect(() => {
    fetchCaptcha();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !captchaToken || captchaLoading) return;
    setError("");
    setLoading(true);
    try {
      const response = await fetch(
        `${API_ENDPOINT}admin/login/${captchaToken}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password, captcha }),
        }
      );
      const result = await response.json();
      if (!response.ok || !result.token)
        throw new Error(
          result.message || "Unable to sign in. Please try again."
        );
      saveToken(result.token);
      navigate("/home", { replace: true });
    } catch (err) {
      setError(err.message);
      await fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="silk-login">
      <section className="silk-login-brand" aria-label="SilkCart admin console">
        <div className="silk-brand-top">
          <img src={SILKCART_LOGO} alt="SilkCart" />
          <span>ADMIN CONSOLE</span>
        </div>
        <div className="silk-brand-story">
          <span className="silk-eyebrow">
            ROOTED IN SERICULTURE. BUILT FOR TOMORROW.
          </span>
          <h1>
            A connected world
            <br />
            of <em>silk.</em>
          </h1>
          <p>
            Bring your community, orders, and operations together. A clearer
            view of everything SilkCart.
          </p>
          <div className="silk-thread-art" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
        <div className="silk-brand-bottom">
          <span>The digital revolution of sericulture.</span>
          <span>SilkCart</span>
        </div>
      </section>
      <section className="silk-login-form-panel">
        <div className="silk-mobile-brand">
          <img src={SILKCART_LOGO} alt="SilkCart" />
          <span>Admin console</span>
        </div>
        <Box className="silk-login-form">
          <div className="silk-login-lock">
            <LockOutlined fontSize="small" />
          </div>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 2 }}
          >
            YOUR WORKSPACE AWAITS
          </Typography>
          <Typography component="h2" variant="h4" sx={{ mt: 1, mb: 1 }}>
            Welcome back.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Sign in to manage your SilkCart operations.
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "grid", gap: 2.5 }}
          >
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              label="Password"
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      <>
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.2 }}>
                Security verification
              </Typography>
              <div className="silk-captcha-row">
                <div
                  className="silk-captcha-image"
                  role="img"
                  aria-label="CAPTCHA verification image"
                >
                  {captchaLoading ? (
                    <CircularProgress size={22} />
                  ) : captchaSvg ? (
                    <span dangerouslySetInnerHTML={{ __html: captchaSvg }} />
                  ) : (
                    <Typography variant="caption">Code unavailable</Typography>
                  )}
                </div>
                <IconButton
                  aria-label="Refresh verification code"
                  onClick={fetchCaptcha}
                  disabled={captchaLoading || loading}
                >
                  <Refresh />
                </IconButton>
                <TextField
                  label="Enter code"
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  required
                  disabled={loading || captchaLoading}
                  inputProps={{
                    maxLength: 5,
                    minLength: 5,
                    autoComplete: "off",
                  }}
                  fullWidth
                />
              </div>
              {captchaError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {captchaError}{" "}
                  <Button size="small" onClick={fetchCaptcha}>
                    Retry
                  </Button>
                </Alert>
              )}
            </div>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || captchaLoading || !captchaToken}
              endIcon={!loading && <ArrowForward />}
              sx={{ py: 1.8 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={19} color="inherit" sx={{ mr: 1 }} />{" "}
                  Signing in…
                </>
              ) : (
                "Sign in to your workspace"
              )}
            </Button>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3.5, textAlign: "center" }}
          >
            Need a hand?{" "}
            <Link
              href="mailto:mysilkcart@gmail.com"
              underline="hover"
              fontWeight={700}
            >
              Contact our team
            </Link>
          </Typography>
        </Box>
        <Typography
          className="silk-login-footer"
          variant="caption"
          color="text.secondary"
        >
          © {new Date().getFullYear()} SilkCart · Administration portal
        </Typography>
      </section>
    </main>
  );
};
export default AdminLogin;
